import { all, call, put, select, takeLatest, race, delay } from 'redux-saga/effects';
import { getUserToken } from '../../pagesNext/live/Helpers';
import { historyPush, historyReplace } from '../../services/History';
import { getDataForExercise, getHintType } from './Selectors';
import { getInitialWordQueue, getTypoPairs } from './services/Misc';
import { processRequest } from '../../services/Api';
import { TYPO_CHECKER_API_URL } from '../../services/Constants';
import { exerciseDetailsActionTypes } from './Constants';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as exerciseDetailsActions from './Actions';
import * as liveActions from '../../pagesNext/live/Actions';

export default function* () {
	yield all([
		yield takeLatest(exerciseDetailsActionTypes.START_EXERCISE_REQUEST, handleStartExercise),
		yield takeLatest(
			exerciseDetailsActionTypes.RESTART_EXERCISE_REQUEST,
			handleRestartExerciseDetails,
		),
		yield takeLatest(exerciseDetailsActionTypes.SKIP_ONBOARDING_REQUEST, handleSkipOnboarding),
		yield takeLatest(exerciseDetailsActionTypes.SEND_ANSWER_REQUEST, handleSendAnswer),
		yield takeLatest(exerciseDetailsActionTypes.SAVE_SETTINGS_REQUEST, handleSaveSettings),
		yield takeLatest(exerciseDetailsActionTypes.SAVE_SUBTYPE_REQUEST, handleSaveSubtype),
		yield takeLatest(
			exerciseDetailsActionTypes.RESUME_PAUSED_EXERCISE_REQUEST,
			handleResumePausedExerciseRequest,
		),
		yield takeLatest(
			exerciseDetailsActionTypes.HANDLE_WORDS_TYPO_CHECKER,
			handleWordsTypeChecker,
		),
	]);
}

export function* handleStartExercise() {
	try {
		const payload = yield select(getDataForExercise);

		if (payload.list_ids.length) {
			const { data } = yield call(processRequest, 'exercises', 'POST', payload);

			yield call(historyReplace, `/exercise/${data.id}`);
			yield put(exerciseDetailsActions.resumePausedExerciseRequest({ id: data.id }));
		} else yield call(historyPush, '/');
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(exerciseDetailsActions.startExerciseError());
	}
}

export function* handleRestartExerciseDetails({ payload }) {
	const { configuration, word_queue } = payload.data || {};

	try {
		const dataForExercise = yield select(getDataForExercise);
		const { id, exercise_type_code } = dataForExercise;
		const { data } = yield call(processRequest, `exercises/${id}/restart`, 'POST', {
			exercise_type_code,
			id,
			configuration,
			word_queue,
		});

		yield call(historyReplace, `/exercise/${data.id}`);
		yield put(exerciseDetailsActions.restartExerciseSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(exerciseDetailsActions.restartExerciseError());
	}
}

export function* handleSkipOnboarding({ payload }) {
	try {
		const { data } = payload;

		yield call(processRequest, 'exercises/skip_onboarding', 'POST', data);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(exerciseDetailsActions.skipOnboardingError());
	}
}

export function* handleSendAnswer({ payload }) {
	try {
		const { data: formData, code } = payload;
		const { requestData, exerciseId, callback } = formData;
		let userToken = null;

		if (code) {
			userToken = getUserToken(code);
		}

		const { data } = yield call(
			processRequest,
			`exercises/${exerciseId}/answer`,
			'POST',
			requestData,
			false,
			false,
			userToken ? { 'X-Live-Battle-Room-Token': userToken } : {},
		);
		const { is_exercise_finished } = requestData || {};
		const { live_battle_answer_summary, streak, result_id, show_app_rating } = data || {};

		if (code) {
			yield put(liveActions.setLiveBattleAnswerSummary(live_battle_answer_summary));
		}

		if (is_exercise_finished) {
			if (streak) {
				yield put(exerciseDetailsActions.sendAnswerSuccess(data, streak));
			} else if (callback) {
				callback(result_id, show_app_rating);
			}
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(exerciseDetailsActions.sendAnswerError());
	}
}

export function* handleSaveSettings({ payload }) {
	try {
		const { data } = payload;
		const { allConfiguration, exerciseId, word_queue } = data;
		const requestPayload = {
			configuration: allConfiguration,
			word_queue,
		};
		yield call(
			processRequest,
			`exercises/${exerciseId}/save_configuration`,
			'POST',
			requestPayload,
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(exerciseDetailsActions.saveSettingsError());
	}
}

export function* handleSaveSubtype() {
	try {
		const payload = yield select(getHintType);
		const { id: exerciseId, hint_type: hintType } = payload;
		yield call(processRequest, `exercises/${exerciseId}/save_subtype`, 'PATCH', {
			exercise_subtype_code: hintType.value,
		});
		yield put(exerciseDetailsActions.setStartExercise());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(exerciseDetailsActions.saveSubtypeError());
	}
}

export function* handleResumePausedExerciseRequest({ payload = {} }) {
	try {
		const { id: exerciseId } = payload;

		const { data } = yield call(processRequest, `exercises/${exerciseId}`);

		yield put(exerciseDetailsActions.resumePausedExerciseSuccess(getInitialWordQueue(data)));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(exerciseDetailsActions.resumePausedExerciseError());
	}
}

export function* handleWordsTypeChecker({ payload }) {
	const { wordTuples } = payload || {};

	try {
		const requestPayload = {
			string_tuples: [wordTuples],
		};

		const { typoCheckerData, timeout } = yield race({
			typoCheckerData: call(
				processRequest,
				TYPO_CHECKER_API_URL,
				'POST',
				requestPayload,
				false,
				true,
			),
			timeout: delay(500),
		});
		if (timeout || typoCheckerData.data.length === 0)
			throw new Error('timeout of 500ms exceeded');

		yield put(exerciseDetailsActions.handleWordsTypoCheckerSuccess(typoCheckerData.data));
	} catch (e) {
		const formattedData = getTypoPairs([wordTuples]);

		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(exerciseDetailsActions.handleWordsTypoCheckerSuccess(formattedData));
	}
}
